export interface PanoramaLivingSection {
  id: string;
  title: string;
  icon: string;
  color: string;
  intro: string;
  tips: PanoramaLivingTip[];
}

export interface PanoramaLivingTip {
  id: string;
  title: string;
  body: string;
  tags?: string[];
  type?: 'tip' | 'recommendation' | 'checklist';
  checklist?: string[];
}

export const panoramaLivingSections: PanoramaLivingSection[] = [
  {
    id: 'living-well',
    title: 'Living Well at Panorama',
    icon: '🏙️',
    color: '#4CC9FF',
    intro: 'A few foundational habits that make daily life at Panorama noticeably better.',
    tips: [
      {
        id: 'lw-1',
        title: 'Use the Building Amenities Intentionally',
        body: 'The pool deck, fitness center, and common areas are genuinely good. Residents who use them regularly tend to feel more connected to the building and get more value from living here. Schedule time for them the same way you would an off-site gym.',
        tags: ['Amenities', 'Lifestyle'],
        type: 'tip',
      },
      {
        id: 'lw-2',
        title: 'Know Your Concierge Team',
        body: 'The front desk and concierge staff are a resource. Introduce yourself, learn their names, and treat them well. They can make a meaningful difference in your day-to-day experience — from package handling to guest coordination.',
        tags: ['Concierge', 'Building Staff'],
        type: 'tip',
      },
      {
        id: 'lw-3',
        title: 'Establish a Quiet Hours Routine',
        body: 'High-rise living means shared walls and shared floors. Being mindful of noise — especially late evenings and early mornings — is both courteous and practical. It also tends to come back to you.',
        tags: ['Etiquette', 'Community'],
        type: 'tip',
      },
    ],
  },
  {
    id: 'household-upgrades',
    title: 'Best Household Upgrades',
    icon: '✨',
    color: '#9B7FFF',
    intro: 'Considered upgrades that make a Panorama unit more comfortable, functional, and enjoyable.',
    tips: [
      {
        id: 'hu-1',
        title: 'Blackout Curtains or Motorized Shades',
        body: 'Las Vegas light is intense. Quality blackout curtains or motorized shades make a significant difference in sleep quality and daytime comfort. Motorized options are particularly useful for high windows.',
        tags: ['Home', 'Sleep', 'Upgrade'],
        type: 'recommendation',
      },
      {
        id: 'hu-2',
        title: 'Air Purifier for Desert Living',
        body: 'Desert air quality varies. A quality HEPA air purifier — particularly during high-wind or dust events — is a worthwhile investment. Brands like Dyson, Coway, and Blueair are well-regarded.',
        tags: ['Air Quality', 'Health', 'Desert Living'],
        type: 'recommendation',
      },
      {
        id: 'hu-3',
        title: 'Smart Home Basics',
        body: 'A smart thermostat (Ecobee or Nest) pays for itself quickly in Las Vegas summers. Smart lighting and a video doorbell are also worth considering for convenience and security.',
        tags: ['Smart Home', 'Energy', 'Convenience'],
        type: 'recommendation',
      },
      {
        id: 'hu-4',
        title: 'Quality Bedding',
        body: 'If you\'re going to invest in one thing for your unit, make it the bed. High-quality sheets, a good mattress topper, and proper pillows make every night better. Brands like Parachute, Brooklinen, and Boll & Branch are resident favorites.',
        tags: ['Bedding', 'Sleep', 'Quality of Life'],
        type: 'recommendation',
      },
    ],
  },
  {
    id: 'premium-everyday',
    title: 'Premium Everyday Recommendations',
    icon: '⭐',
    color: '#2DD4A0',
    intro: 'Small upgrades to daily routines that compound into a noticeably better quality of life.',
    tips: [
      {
        id: 'pe-1',
        title: 'Upgrade Your Coffee Setup',
        body: 'A quality home espresso or pour-over setup eliminates the need for daily coffee runs and produces a better result. Nespresso Vertuo is the low-effort option; a Breville Barista Express is the enthusiast choice.',
        tags: ['Coffee', 'Daily Routine', 'Home'],
        type: 'recommendation',
      },
      {
        id: 'pe-2',
        title: 'Grocery Delivery as a Default',
        body: 'Instacart, Amazon Fresh, and Whole Foods delivery are all reliable in the Las Vegas area. Setting up a weekly recurring order for staples saves time and reduces impulse purchases.',
        tags: ['Grocery', 'Delivery', 'Convenience'],
        type: 'tip',
      },
      {
        id: 'pe-3',
        title: 'Invest in a Good Water Filter',
        body: 'Las Vegas tap water is safe but heavily treated. A quality under-sink or countertop filter (Berkey, Brita Pro, or reverse osmosis) makes a noticeable difference in taste and reduces plastic bottle use.',
        tags: ['Water', 'Health', 'Home'],
        type: 'recommendation',
      },
    ],
  },
  {
    id: 'travel-away',
    title: 'Travel-Away Checklist',
    icon: '✈️',
    color: '#F0A832',
    intro: 'A practical checklist for leaving your Panorama unit for an extended period.',
    tips: [
      {
        id: 'ta-1',
        title: 'Before You Leave',
        body: 'A consistent pre-departure routine prevents the anxiety of wondering if you left something on.',
        type: 'checklist',
        checklist: [
          'Set thermostat to 78°F (summer) or 65°F (winter) — not off',
          'Unplug non-essential electronics',
          'Empty and clean the refrigerator if away more than 2 weeks',
          'Run the garbage disposal and clean the drain',
          'Set up USPS mail hold or forward at usps.com',
          'Notify the front desk of your departure and expected return',
          'Leave an emergency contact with the concierge',
          'Check that all windows and the balcony door are locked',
          'Set smart home devices to away mode',
          'Arrange for a trusted person to check the unit if away more than 2 weeks',
        ],
        tags: ['Travel', 'Checklist', 'Second Home'],
      },
      {
        id: 'ta-2',
        title: 'USPS Informed Delivery',
        body: 'Sign up for USPS Informed Delivery at usps.com — it\'s free and sends you daily email scans of incoming mail. Essential for second-home owners and frequent travelers. You\'ll know what\'s waiting before you return.',
        tags: ['Mail', 'USPS', 'Travel'],
        type: 'tip',
      },
    ],
  },
  {
    id: 'second-home-tips',
    title: 'Second-Home Owner Tips',
    icon: '🔑',
    color: '#4CC9FF',
    intro: 'Practical guidance for residents who use Panorama as a second home or split their time between cities.',
    tips: [
      {
        id: 'sh-1',
        title: 'Establish a Building Relationship Before You Need It',
        body: 'Second-home owners who have a relationship with the concierge and front desk team are far better positioned when something goes wrong. Introduce yourself, provide your contact information, and check in when you arrive.',
        tags: ['Second Home', 'Building Relationship'],
        type: 'tip',
      },
      {
        id: 'sh-2',
        title: 'Consider a Property Check Service',
        body: 'For extended absences, a property check service provides peace of mind. These services inspect your unit, handle minor issues, and report back. Ask in the community for current resident recommendations.',
        tags: ['Property Check', 'Second Home', 'Absentee'],
        type: 'tip',
      },
      {
        id: 'sh-3',
        title: 'Keep a Stocked Essentials Kit',
        body: 'A small kit of essentials — basic tools, cleaning supplies, a spare set of keys, and a first-aid kit — means you\'re not scrambling when you arrive. Store it in a consistent location.',
        tags: ['Preparation', 'Second Home', 'Essentials'],
        type: 'recommendation',
      },
      {
        id: 'sh-4',
        title: 'Understand Your HOA and Building Rules',
        body: 'Second-home owners sometimes miss building communications. Make sure your contact information is current with the HOA and that you\'re receiving all building notices. This is especially important for rule changes and assessments.',
        tags: ['HOA', 'Rules', 'Second Home'],
        type: 'tip',
      },
    ],
  },
  {
    id: 'mail-delivery',
    title: 'Mail & Delivery Tips',
    icon: '📦',
    color: '#9B7FFF',
    intro: 'How to manage mail and deliveries effectively in a high-rise building.',
    tips: [
      {
        id: 'md-1',
        title: 'Use Your Unit Number Consistently',
        body: 'Always include your full unit number in your address. Inconsistent formatting causes delivery issues. The correct format is: [Your Name], [Tower Address], Unit [Number], Las Vegas, NV [ZIP].',
        tags: ['Mail', 'Delivery', 'Address'],
        type: 'tip',
      },
      {
        id: 'md-2',
        title: 'Amazon Locker and Hub',
        body: 'For frequent Amazon orders, consider using an Amazon Hub Locker or Counter location nearby. This eliminates the risk of packages sitting in the lobby and provides more flexible pickup timing.',
        tags: ['Amazon', 'Delivery', 'Packages'],
        type: 'tip',
      },
      {
        id: 'md-3',
        title: 'Notify the Front Desk for Large Deliveries',
        body: 'For furniture, appliances, or other large deliveries, notify the front desk in advance. This allows them to coordinate elevator access and ensure a smooth delivery experience.',
        tags: ['Large Delivery', 'Coordination', 'Building'],
        type: 'tip',
      },
    ],
  },
  {
    id: 'building-lifestyle',
    title: 'Building Lifestyle Tips',
    icon: '🏢',
    color: '#2DD4A0',
    intro: 'How to get the most out of living in a premium high-rise community.',
    tips: [
      {
        id: 'bl-1',
        title: 'Learn the Elevator Patterns',
        body: 'High-rise elevator timing has patterns. Morning rush (7–9am) and evening peak (5–7pm) are the busiest. If you have flexibility, shifting your schedule slightly makes a meaningful difference.',
        tags: ['Building', 'Daily Life', 'Practical'],
        type: 'tip',
      },
      {
        id: 'bl-2',
        title: 'Respect Shared Spaces',
        body: 'The pool deck, gym, and common areas are shared by all residents. Leaving them as you found them — or better — is both courteous and contributes to the overall quality of the building.',
        tags: ['Etiquette', 'Community', 'Shared Spaces'],
        type: 'tip',
      },
      {
        id: 'bl-3',
        title: 'Connect with Your Neighbors',
        body: 'High-rise buildings can feel anonymous. Making an effort to know your immediate neighbors — even just by name — creates a better living environment and a useful network for practical matters.',
        tags: ['Community', 'Neighbors', 'Connection'],
        type: 'tip',
      },
    ],
  },
  {
    id: 'small-habits',
    title: 'Small Habits That Improve Daily Living',
    icon: '💡',
    color: '#F0A832',
    intro: 'Minor adjustments that compound into a noticeably better quality of life at Panorama.',
    tips: [
      {
        id: 'smh-1',
        title: 'Morning Balcony Time',
        body: 'If your unit has a balcony, 10 minutes of morning coffee outside is one of the best things about high-rise living. The Las Vegas morning light is genuinely beautiful before the heat sets in.',
        tags: ['Morning Routine', 'Balcony', 'Lifestyle'],
        type: 'tip',
      },
      {
        id: 'smh-2',
        title: 'Keep Common Areas Clean and Considerate',
        body: 'High-rise living means shared spaces. Being mindful of how you leave common areas — elevators, hallways, the pool deck — keeps the building pleasant for everyone and reflects well on the community.',
        tags: ['Etiquette', 'Building', 'Habits'],
        type: 'tip',
      },
      {
        id: 'smh-3',
        title: 'Use the Community Platform',
        body: 'This platform exists because residents who are connected to their community have a better experience. Checking in occasionally — even just to browse the marketplace or read the newsletter — keeps you informed and connected.',
        tags: ['Community', 'Platform', 'Connection'],
        type: 'tip',
      },
    ],
  },
];
